import { computeBatteryStacks, isSeriesStacked, type BatteryStacks } from "./battery-stack";
import { type DefLookup } from "./catalog";
import { intraLinks } from "./diagnostics/graph";
import { effectiveTerminals } from "./effective";
import type {
  Cable,
  ComponentDefinition,
  ComponentNode,
  SystemNode,
  System,
} from "./types";

/** Fallback for a domain with nothing declaring a voltage. */
export const DEFAULT_NOMINAL_V = 12;

/**
 * A maximal set of (node, terminal) vertices connected through cables and
 * passthrough components.
 */
export type VoltageDomain = {
  id: string;
  /** Nominal DC voltage of the domain (V). */
  nominalV: number;
  /** Did anything inside the domain set the voltage, or is it the fallback? */
  hasSource: boolean;
  /** Set of `nodeId:terminalId` keys reachable inside this domain. */
  terminalKeys: ReadonlySet<string>;
  /** Cable ids whose *both* endpoints lie inside this domain. */
  cableIds: ReadonlySet<string>;
  /** Node ids with at least one terminal here — a converter appears in two. */
  nodeIds: ReadonlySet<string>;
};

type TermKey = `${string}:${string}`;
const tk = (nodeId: string, termId: string): TermKey => `${nodeId}:${termId}`;

/**
 * Categories whose input terminals sit in one domain and outputs in another.
 * Distribution parts are NOT boundaries — a Lynx Distributor's studs are all
 * one bus.
 */
function isVoltageBoundary(def: ComponentDefinition): boolean {
  if (def.category === "charger") return true;
  // An alternator is a source with a single output, so it bridges nothing.
  return false;
}

/**
 * Terminals of one component at the same potential as `termId`, for a
 * voltage walk. Distribution parts and shunts follow the conductors declared
 * by `intraLinks`, keeping independent poles separate even when the component
 * is drawn as a passthrough switch. This lets a 24 V bank's voltage reach
 * what sits past the main fuse instead of stopping at the holder and leaving
 * the rest of the boat on the 12 V fallback. Signal sense taps are never
 * bridged: merging them pulls the two banks a BMV senses into one domain.
 * Solar panels bridge nothing here — series and parallel look identical one
 * step at a time, and PV voltage belongs to `cable-voltage.ts`.
 */
export function voltageBridgedTerminals(
  node: ComponentNode,
  def: ComponentDefinition,
  termId: string,
): string[] {
  if (isVoltageBoundary(def)) return [];
  // Effective terminals, so a grown bus bar's `stud-N` bridges to its studs.
  const terminals = effectiveTerminals(def, node);
  const self = terminals.find((t) => t.id === termId);
  if (self?.polarity === "signal") return [];
  const out: string[] = [];
  for (const link of intraLinks(node, def)) {
    const other =
      link.from === termId ? link.to : link.to === termId ? link.from : undefined;
    if (other === undefined) continue;
    const t = terminals.find((x) => x.id === other);
    if (!t || t.polarity === "signal") continue;
    out.push(other);
  }
  return out;
}

/** Neighbours inside one domain: across cables and through the part itself.
 *  Stops at a boundary — a charger's pv/in does not bridge to its batt/out. */
function neighbours(
  nodeId: string,
  termId: string,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: DefLookup,
): Array<{ nodeId: string; termId: string }> {
  const out: Array<{ nodeId: string; termId: string }> = [];
  for (const c of cables) {
    if (c.fromNodeId === nodeId && c.fromTerminalId === termId) {
      out.push({ nodeId: c.toNodeId, termId: c.toTerminalId });
    } else if (c.toNodeId === nodeId && c.toTerminalId === termId) {
      out.push({ nodeId: c.fromNodeId, termId: c.fromTerminalId });
    }
  }
  const node = nodesById[nodeId];
  if (node?.kind !== "component") return out;
  const def = defLookup(node.definitionId, node.customDefinition);
  if (!def) return out;
  for (const t of voltageBridgedTerminals(node, def, termId)) {
    out.push({ nodeId, termId: t });
  }
  return out;
}

/** A battery's own pos↔neg bridges only when it is a cell of a series stack
 *  (`battery-stack.ts` — a `seriesLink` cable, a + landed on the next cell's −
 *  through a junction, a 2P8S block) — without that, a lone battery's + and −
 *  collapse into one domain. */
function batteryBridges(
  nodeId: string,
  termId: string,
  nodesById: Record<string, SystemNode>,
  stacks: BatteryStacks,
  defLookup: DefLookup,
): Array<{ nodeId: string; termId: string }> {
  const node = nodesById[nodeId];
  if (node?.kind !== "component") return [];
  const def = defLookup(node.definitionId, node.customDefinition);
  if (!def || def.category !== "battery") return [];
  if (!isSeriesStacked(stacks, nodeId)) return [];
  const out: Array<{ nodeId: string; termId: string }> = [];
  for (const t of def.terminals) {
    if (t.id === termId) continue;
    if (t.polarity !== "positive" && t.polarity !== "negative") continue;
    out.push({ nodeId, termId: t.id });
  }
  return out;
}

function floodDomain(
  seedNodeId: string,
  seedTermId: string,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: DefLookup,
  stacks: BatteryStacks,
): Set<TermKey> {
  const visited = new Set<TermKey>();
  const queue: Array<{ nodeId: string; termId: string }> = [
    { nodeId: seedNodeId, termId: seedTermId },
  ];
  while (queue.length) {
    const v = queue.shift()!;
    const k = tk(v.nodeId, v.termId);
    if (visited.has(k)) continue;
    visited.add(k);
    for (const n of neighbours(v.nodeId, v.termId, nodesById, cables, defLookup)) {
      queue.push(n);
    }
    for (const n of batteryBridges(v.nodeId, v.termId, nodesById, stacks, defLookup)) {
      queue.push(n);
    }
  }
  return visited;
}

/** Voltage a component establishes. Only batteries — counting solar here would
 *  double-count panels in a parallel feed. */
function declaredVoltage(def: ComponentDefinition): number | undefined {
  if (def.category === "battery") return def.voltage;
  return undefined;
}

/**
 * Bus voltage from series-stacked batteries: summed along **one** string's
 * levels (`battery-stack.ts`), never across the domain — a 2S2P bank of four
 * 6 V cells is 12 V, not 24 V. The tallest stack wins.
 */
function stackedBatteryVoltage(
  terminalKeys: Set<TermKey>,
  stacks: BatteryStacks,
  nodesById: Record<string, SystemNode>,
  defLookup: DefLookup,
): number | undefined {
  const battTerms = new Map<string, Set<string>>();
  for (const k of terminalKeys) {
    const idx = k.indexOf(":");
    const nodeId = k.slice(0, idx);
    const termId = k.slice(idx + 1);
    const node = nodesById[nodeId];
    if (node?.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def || def.category !== "battery") continue;
    if (!battTerms.has(nodeId)) battTerms.set(nodeId, new Set());
    battTerms.get(nodeId)!.add(termId);
  }
  if (battTerms.size === 0) return undefined;

  // Only batteries whose every power terminal is inside this domain.
  let tallest: number | undefined;
  for (const [nodeId, terms] of battTerms) {
    const node = nodesById[nodeId];
    if (node?.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    const powerTermCount = def.terminals.filter(
      (t) => t.polarity === "positive" || t.polarity === "negative",
    ).length;
    if (terms.size !== powerTermCount) continue;
    const stack = stacks.stackOfBattery.get(nodeId);
    if (!stack || stack.levels.length < 2) continue;
    if (tallest == null || stack.nominalV > tallest) tallest = stack.nominalV;
  }
  return tallest;
}

function resolveDomainVoltage(
  terminalKeys: Set<TermKey>,
  stacks: BatteryStacks,
  nodesById: Record<string, SystemNode>,
  defLookup: DefLookup,
  defaultV: number,
): { nominalV: number; hasSource: boolean } {
  const stacked = stackedBatteryVoltage(terminalKeys, stacks, nodesById, defLookup);
  if (stacked && stacked > 0) return { nominalV: stacked, hasSource: true };
  let best: number | undefined;
  for (const k of terminalKeys) {
    const idx = k.indexOf(":");
    const nodeId = k.slice(0, idx);
    const node = nodesById[nodeId];
    if (node?.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    const v = declaredVoltage(def);
    if (v != null && (best == null || v > best)) best = v;
  }
  if (best != null && best > 0) return { nominalV: best, hasSource: true };
  return { nominalV: defaultV, hasSource: false };
}

/** Every vertex a cable lands on, plus power terminals of batteries, chargers
 *  and solar — so an unwired converter still appears in both its domains. */
function* allSeedVertices(
  system: System,
  defLookup: DefLookup,
): Generator<{ nodeId: string; termId: string }> {
  const seen = new Set<string>();
  const emit = (nodeId: string, termId: string) => {
    const k = tk(nodeId, termId);
    if (seen.has(k)) return;
    seen.add(k);
  };
  for (const c of system.cables) {
    emit(c.fromNodeId, c.fromTerminalId);
    yield { nodeId: c.fromNodeId, termId: c.fromTerminalId };
    emit(c.toNodeId, c.toTerminalId);
    yield { nodeId: c.toNodeId, termId: c.toTerminalId };
  }
  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def) continue;
    if (
      def.category === "battery" ||
      def.category === "charger" ||
      def.category === "solar"
    ) {
      for (const t of def.terminals) {
        if (t.polarity === "signal") continue;
        if (!seen.has(tk(n.id, t.id))) {
          seen.add(tk(n.id, t.id));
          yield { nodeId: n.id, termId: t.id };
        }
      }
    }
  }
}

export type VoltageDomainMap = {
  domains: VoltageDomain[];
  domainByTerminal: ReadonlyMap<string, VoltageDomain>;
  domainByCable: ReadonlyMap<string, VoltageDomain>;
};

/** A cable maps to a domain only when both endpoints are inside it, so one
 *  spanning two domains is absent from `domainByCable`. */
export function resolveVoltageDomains(
  system: System,
  defLookup: DefLookup,
  defaultV: number = DEFAULT_NOMINAL_V,
): VoltageDomainMap {
  const nodesById: Record<string, SystemNode> = {};
  for (const n of system.nodes) nodesById[n.id] = n;
  const stacks = computeBatteryStacks(system, defLookup);

  const visited = new Set<TermKey>();
  const domains: VoltageDomain[] = [];
  const domainByTerminal = new Map<string, VoltageDomain>();
  const domainByCable = new Map<string, VoltageDomain>();

  let nextId = 0;
  for (const seed of allSeedVertices(system, defLookup)) {
    const k = tk(seed.nodeId, seed.termId);
    if (visited.has(k)) continue;
    const keys = floodDomain(
      seed.nodeId,
      seed.termId,
      nodesById,
      system.cables,
      defLookup,
      stacks,
    );
    for (const x of keys) visited.add(x);
    const { nominalV, hasSource } = resolveDomainVoltage(
      keys,
      stacks,
      nodesById,
      defLookup,
      defaultV,
    );
    const nodeIds = new Set<string>();
    for (const tkn of keys) {
      const i = tkn.indexOf(":");
      nodeIds.add(tkn.slice(0, i));
    }
    const cableIds = new Set<string>();
    for (const c of system.cables) {
      if (
        keys.has(tk(c.fromNodeId, c.fromTerminalId)) &&
        keys.has(tk(c.toNodeId, c.toTerminalId))
      ) {
        cableIds.add(c.id);
      }
    }
    const domain: VoltageDomain = {
      id: `dom-${nextId++}`,
      nominalV,
      hasSource,
      terminalKeys: keys,
      cableIds,
      nodeIds,
    };
    domains.push(domain);
    for (const tkn of keys) domainByTerminal.set(tkn, domain);
    for (const cid of cableIds) domainByCable.set(cid, domain);
  }

  return { domains, domainByTerminal, domainByCable };
}

/** Nominal voltage of one cable; a cross-domain bond falls back to `defaultV`. */
export function cableNominalVoltage(
  cableId: string,
  domains: VoltageDomainMap,
  defaultV: number = DEFAULT_NOMINAL_V,
): number {
  const d = domains.domainByCable.get(cableId);
  return d?.nominalV ?? defaultV;
}

/** Snap a voltage to its nominal class (12 / 24 / 48). Classification only —
 *  display the real value, or an 18 V PV string reads back as 24 V. */
export function nominalClass(v: number): number {
  if (v <= 15) return 12;
  if (v <= 30) return 24;
  if (v <= 60) return 48;
  return Math.round(v);
}

/**
 * The system's main bus voltage: the highest source-backed domain. Every "is
 * that cable off-bus?" check must ask this, never a hardcoded 12 — a lithium
 * bank declares 12.8 V and would fire on every cable in the system.
 */
export function busNominalVoltage(domains: VoltageDomainMap): number {
  let busV = DEFAULT_NOMINAL_V;
  for (const d of domains.domains) {
    if (d.hasSource && d.nominalV > busV) busV = d.nominalV;
  }
  return busV;
}
